import GeneralDialog from '../../../shared/general-dialog';
import { ChromeDeviceModel } from '../../../../types';
import AvatarChip from '../../../shared/avatar-chip';
import dayjs from 'dayjs';
import { Gauge } from '@mui/x-charts/Gauge';
import StatusLabel, { StatusType } from '../../../shared/status-label';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LaptopChromebook from '@mui/icons-material/LaptopChromebook';

interface ChromeDeviceDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDevice: ChromeDeviceModel;
}

export default function ChromeDeviceDialog({
  open,
  handleClose,
  selectedDevice,
}: ChromeDeviceDialogProps) {
  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title="Device Details"
      maxWidth="md"
      dialogContent={
        <>
          <Stack gap={1}>
            <Card>
              <CardContent>
                <Stack
                  flexDirection={'row'}
                  gap={2}
                  justifyContent={'space-between'}
                >
                  <Stack flexDirection={'column'} gap={0.5}>
                    <Stack
                      flexDirection={'row'}
                      gap={2}
                      pb={1}
                      alignItems={'center'}
                    >
                      <LaptopChromebook sx={{ width: 40, height: 40 }} />
                      <Typography variant="h6">
                        {selectedDevice.model}
                      </Typography>
                    </Stack>

                    <Typography defaultValue={`${selectedDevice.deviceId}`} />

                    <Stack flexDirection={'row'} gap={8} alignItems={'center'}>
                      <TextField
                        id="outlined-read-only-input"
                        label="Serial Number"
                        defaultValue={`${selectedDevice.serialNumber}`}
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                        variant="standard"
                        size="small"
                      />
                      {selectedDevice.diskSpaceUsage ? (
                        <Stack flexDirection={'row'} alignItems={'center'}>
                          {selectedDevice.diskSpaceUsage?.usedBytes &&
                            selectedDevice.diskSpaceUsage?.capacityBytes && (
                              <Stack
                                flexDirection={'row'}
                                alignItems={'center'}
                                gap={1}
                              >
                                <Gauge
                                  width={60}
                                  height={60}
                                  value={Math.round(
                                    (parseInt(
                                      selectedDevice.diskSpaceUsage.usedBytes,
                                      10
                                    ) /
                                      parseInt(
                                        selectedDevice.diskSpaceUsage
                                          .capacityBytes,
                                        10
                                      )) *
                                      100
                                  )}
                                  text={({ value }) =>
                                    value ? value + `%` : '0%'
                                  }
                                />
                                <Typography sx={{ lineHeight: 1 }}>
                                  Disk Space Used
                                </Typography>
                              </Stack>
                            )}
                        </Stack>
                      ) : null}
                    </Stack>
                  </Stack>
                  <Stack
                    flexDirection={'column'}
                    justifyContent={'space-between'}
                    alignItems={'flex-end'}
                  >
                    <StatusLabel status={selectedDevice.status as StatusType} />
                    <Typography sx={{ fontSize: 10 }} pt={1}>
                      Last Sync:{' '}
                      {selectedDevice.lastSync
                        ? new Date(
                            selectedDevice.lastSync.value
                          ).toLocaleString()
                        : 'No sync data'}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack flexDirection="row" gap={4} useFlexGap flexWrap="wrap">
                  <Stack flexDirection={'column'}>
                    <TextField
                      id="outlined-read-only-input"
                      label="Auto Renew"
                      defaultValue={selectedDevice.willAutoRenew ? 'Yes' : 'No'}
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                      sx={{ width: '350px' }}
                    />
                    <TextField
                      id="outlined-read-only-input"
                      label="OS Version"
                      defaultValue={
                        selectedDevice.osVersion
                          ? selectedDevice.osVersion
                          : 'No OS Version Listed'
                      }
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                      sx={{ width: '350px' }}
                    />
                    <TextField
                      id="outlined-read-only-input"
                      label="Platform Version"
                      defaultValue={
                        selectedDevice.platformVersion
                          ? selectedDevice.platformVersion
                          : 'No Platform Version Listed'
                      }
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                      sx={{ width: '350px' }}
                    />
                    <TextField
                      id="outlined-read-only-input"
                      label="Firmware Version"
                      defaultValue={
                        selectedDevice.firmwareVersion
                          ? selectedDevice.firmwareVersion
                          : 'No Firmware Version Listed'
                      }
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                      sx={{ width: '350px' }}
                    />
                    <TextField
                      id="outlined-read-only-input"
                      label="Mac Address"
                      defaultValue={
                        selectedDevice.macAddress
                          ? selectedDevice.macAddress
                          : 'No Mac Address Listed'
                      }
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                      sx={{ width: '350px' }}
                    />
                    <TextField
                      id="outlined-read-only-input"
                      label="Used Disk Space"
                      defaultValue={
                        selectedDevice.diskSpaceUsage?.usedBytes
                          ? (() => {
                              const bytes = parseInt(
                                selectedDevice.diskSpaceUsage.usedBytes,
                                10
                              );
                              const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
                              if (bytes === 0) return '';
                              const i = Math.floor(
                                Math.log(bytes) / Math.log(1024)
                              );
                              if (i === 0) return `${bytes} ${sizes[i]}`;
                              return `${(bytes / Math.pow(1024, i)).toFixed(
                                0
                              )} ${sizes[i]}`;
                            })()
                          : 'Unknown'
                      }
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                    />
                  </Stack>

                  <Stack flexDirection={'column'}>
                    <TextField
                      id="outlined-read-only-input"
                      label="Boot Mode"
                      defaultValue={
                        selectedDevice.bootMode
                          ? selectedDevice.bootMode
                          : 'No Boot Mode Listed'
                      }
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                      sx={{ width: '350px' }}
                    />
                    <TextField
                      id="outlined-read-only-input"
                      label="Enrollment Time"
                      defaultValue={
                        selectedDevice.lastEnrollmentTime
                          ? dayjs(
                              new Date(selectedDevice.lastEnrollmentTime.value)
                            ).format('MMM D, YYYY, h:mm A')
                          : 'No Enrollment time listed'
                      }
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                      sx={{ width: '350px' }}
                    />
                    <TextField
                      id="outlined-read-only-input"
                      label="Location"
                      defaultValue={
                        selectedDevice.annotatedLocation
                          ? `${selectedDevice.annotatedLocation}`
                          : 'No location listed'
                      }
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                    />
                    <TextField
                      id="outlined-read-only-input"
                      label="Asset ID"
                      defaultValue={
                        selectedDevice.annotatedAssetId
                          ? selectedDevice.annotatedAssetId
                          : 'No Asset Id Listed'
                      }
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                    />
                    <TextField
                      id="outlined-read-only-input"
                      label="Org Unit Path"
                      defaultValue={`${selectedDevice.orgUnitPath}`}
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                    />
                    <TextField
                      id="outlined-read-only-input"
                      label="Total Disk Space"
                      defaultValue={
                        selectedDevice.diskSpaceUsage?.capacityBytes
                          ? (() => {
                              const bytes = parseInt(
                                selectedDevice.diskSpaceUsage.capacityBytes,
                                10
                              );
                              const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
                              if (bytes === 0) return '';
                              const i = Math.floor(
                                Math.log(bytes) / Math.log(1024)
                              );
                              if (i === 0) return `${bytes} ${sizes[i]}`;
                              return `${(bytes / Math.pow(1024, i)).toFixed(
                                0
                              )} ${sizes[i]}`;
                            })()
                          : 'Unknown'
                      }
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                      }}
                      variant="standard"
                      size="small"
                    />
                  </Stack>
                </Stack>

                <Stack flexDirection={'row'} gap={6} pt={2}>
                  {selectedDevice.recentUsers === null ? (
                    <Stack flexDirection={'column'}>
                      <Typography
                        fontWeight={400}
                        sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                        fontSize={'12px'}
                      >
                        Recent Users
                      </Typography>
                      <Typography>No Recent Users</Typography>
                    </Stack>
                  ) : (
                    <Stack flexWrap="wrap" sx={{ width: 'max-content' }}>
                      <Typography
                        fontWeight={400}
                        sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                        fontSize={'12px'}
                      >
                        Recent Users
                      </Typography>
                      <Stack gap={1}>
                        {selectedDevice.recentUsers
                          ?.filter((user) => user.type === 'USER_TYPE_MANAGED')
                          .map((user) => (
                            <AvatarChip
                              key={user.email}
                              type="User"
                              email={user.email}
                            />
                          ))}
                      </Stack>
                    </Stack>
                  )}

                  <Stack flexDirection={'column'}>
                    <Typography
                      fontWeight={400}
                      sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                      fontSize={'12px'}
                    >
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {selectedDevice.notes ? selectedDevice.notes : 'No Notes'}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </>
      }
      primaryButton={{
        loading: false,
        action: handleClose,
        disabled: false,
        title: 'Close',
      }}
    />
  );
}
